import { useState } from "react";
import { Box, Button, Checkbox, Dialog, FormControlLabel, IconButton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { RegisterFormField } from "../../../../payment.types";
import { PaymentPopUpCloseIcon } from "../../../../../../assets/icons/MUI/PaymentIcons";

const defaultOtherFields = [
  "dateOfBirth",
  "company",
  "gender",
  "address",
  "state",
  "city",
  "zipCode",
  "country",
  "nationality",
];

const AnotherFieldPopUp = ({
  onClose,
  open,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (fields: RegisterFormField[]) => void;
}) => {
  const { t } = useTranslation();

  const [selectedFields, setSelectedFields] = useState(
    defaultOtherFields.map((field) => ({
      title: field,
      inserted: false,
      required: false,
    }))
  );

  const handleCheckboxChange = (fieldTitle: string, type: "inserted" | "required") => {
    setSelectedFields((prev) =>
      prev.map((field) => (field.title === fieldTitle ? { ...field, [type]: !field[type] } : field))
    );
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{
        "& .MuiDialog-paper": {
          width: "100%",
          maxWidth: "640px",
          borderRadius: "12px",
          padding: "24px",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          paddingBottom: "16px",
          borderBottom: "1px solid #E4E7EC",
          textAlign: "center",
        }}
      >
        <Typography component="h3" sx={{ color: "#101828", fontSize: "18px", fontWeight: 600 }}>
          {t("pages.payment.createPaymentLink.addField")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: "16px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <PaymentPopUpCloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            background: "#F9FAFB",
            padding: "0 12px",
            borderBottom: "1px solid #E4E7EC",
          }}
        >
          <FormControlLabel
            sx={{ flex: 1, borderRight: "1px solid #E4E7EC", padding: "8px" }}
            control={<Checkbox disabled />}
            label={t(`pages.payment.createPaymentLink.inputField`)}
            disabled
          />

          <FormControlLabel
            sx={{ flex: 1 }}
            control={<Checkbox />}
            label={t(`pages.payment.createPaymentLink.required`)}
            disabled
          />
        </Box>
        {selectedFields.map((field) => (
          <Box
            key={field.title}
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
              borderBottom: "1px solid #E4E7EC",
            }}
          >
            <FormControlLabel
              sx={{ flex: 1, borderRight: "1px solid #E4E7EC", padding: "8px" }}
              control={
                <Checkbox checked={field.inserted} onChange={() => handleCheckboxChange(field.title, "inserted")} />
              }
              label={t(`pages.payment.createPaymentLink.${field.title}`)}
            />

            <FormControlLabel
              sx={{ flex: 1 }}
              control={
                <Checkbox checked={field.required} onChange={() => handleCheckboxChange(field.title, "required")} />
              }
              label=""
              disabled={!field.inserted}
            />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
        <Button variant="outlined-md" onClick={onClose}>
          {t("pages.payment.createPaymentLink.cancel")}
        </Button>
        <Button
          variant="primary-md"
          onClick={() => {
            const newFields = selectedFields.filter((f) => f.inserted);
            onDone(newFields);
          }}
          disabled={!selectedFields.some((f) => f.inserted)}
        >
          {t("pages.payment.createPaymentLink.done")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AnotherFieldPopUp;
